import React, { memo } from "react";
import { $Button, $Tooltip, $Popconfirm } from "us.common/components";
import { DeleteOutlined, ReloadOutlined, ScissorOutlined } from "us.icons";

export type PageType = {
  url: string;
  pageIndex?: number;
  id?: string | number;
};

export type SplitDocItemProps = {
  page: PageType;
  idx: number;
  isLast: boolean;
  markerActive: boolean;
  rotation: number;
  previewNotSupported: string;
  deleteLabel?: string;
  confirmDeleteLabel?: string;
  yesLabel?: string;
  noLabel?: string;
  rotateLabel?: string;
  splitLabel?: string;
  onToggle: (idx: number) => void;
  onDelete: (idx: number) => void;
  onRotate: (idx: number) => void;
  onPreview: (idx: number) => void;
};
/**
 * `SplitDocItem` renders a single page preview inside the split UI.
 *
 * - Shows a small page preview in an iframe with optional rotation applied.
 * - Provides controls to rotate the page, delete the page, and toggle a split marker.
 * - Clicking the preview invokes `onPreview` to open the larger modal preview.
 *
 * @param {SplitDocItemProps} props Component properties describing page state and callbacks
 * @returns {JSX.Element} The rendered preview card with controls
 */
export const SplitDocItem = memo(
  ({
    page,
    idx,
    isLast,
    markerActive,
    rotation,
    previewNotSupported,
    deleteLabel = "Delete",
    confirmDeleteLabel = "Are you sure you want to delete this page?",
    yesLabel = "Yes",
    noLabel = "No",
    rotateLabel = "Rotate",
    splitLabel = "Split here",
    onToggle,
    onDelete,
    onRotate,
    onPreview,
  }: SplitDocItemProps) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPreview(idx);
      }
    };

    return (
      <div className="split-doc-item-root">
        <div
          className="split-preview-card"
          onClick={() => onPreview(idx)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-label={`Open preview for page ${
            page.pageIndex !== undefined ? page.pageIndex + 1 : idx + 1
          }`}
        >
          <div className="preview-badge">
            {page.pageIndex !== undefined ? page.pageIndex + 1 : idx + 1}
          </div>

          <div
            className="preview-controls"
            onClick={(e) => e.stopPropagation()}
          >
            <$Tooltip title={rotateLabel} placement="left">
              <$Button
                size="small"
                type="default"
                className="preview-button rotate"
                icon={<ReloadOutlined className="rotate-icon" />}
                onClick={() => onRotate(idx)}
              />
            </$Tooltip>

            <$Tooltip title={deleteLabel} placement="left">
              <$Popconfirm
                title={confirmDeleteLabel}
                onConfirm={() => onDelete(idx)}
                okText={yesLabel}
                cancelText={noLabel}
              >
                <$Button
                  size="small"
                  type="default"
                  danger
                  className="preview-button delete"
                  icon={<DeleteOutlined className="delete-icon" />}
                />
              </$Popconfirm>
            </$Tooltip>
          </div>

          {rotation !== 0 && (
            <div className="rotation-indicator">{rotation}°</div>
          )}

          <iframe
            title={`preview-${idx}`}
            src={`${page.url}#toolbar=0&navpanes=0&scrollbar=0`}
            className={`split-preview-iframe rot-${rotation}`}
          >
            <p>{previewNotSupported}</p>
          </iframe>
        </div>

        {!isLast && (
          <div className={`split-toggle ${markerActive ? "active" : ""}`}>
            <$Tooltip title={splitLabel} placement="top">
              <div
                role="button"
                aria-label={`toggle-split-${idx}`}
                onClick={() => onToggle(idx)}
                className="split-toggle-btn"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(idx);
                  }
                }}
              >
                <ScissorOutlined className="scissor-icon" />
              </div>
            </$Tooltip>
          </div>
        )}
      </div>
    );
  },
);

export default SplitDocItem;
